import { useEffect, useState } from "react";
import { api, apiError } from "./api";
import { toast } from "sonner";
import { ShieldAlert, Flag, Ban, X, Check } from "lucide-react";

const REASONS = ["Spam", "Harassment or bullying", "Hate speech",
  "Inappropriate or explicit content", "Violence or threats", "Other"];

// Full-screen EULA gate — must accept before using the app (Apple UGC requirement).
export function TermsGate({ onAccepted }) {
  const [terms, setTerms] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/legal/terms").then((r) => setTerms(r.data)).catch(() => {}); }, []);
  const accept = () => {
    setBusy(true);
    api.post("/legal/accept").then(() => { toast.success("Thanks — terms accepted"); onAccepted(); })
      .catch((e) => toast.error(apiError(e, "Could not save"))).finally(() => setBusy(false));
  };
  return (
    <div className="fixed inset-0 z-[100] bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="terms-gate">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col overflow-hidden">
        <div className="flex items-center gap-2 px-5 py-4 border-b border-gray-100">
          <ShieldAlert className="text-indigo-600" size={20} />
          <h2 className="text-lg font-bold text-gray-900">Terms of Use &amp; Community Guidelines</h2>
        </div>
        <div className="px-5 py-4 overflow-y-auto text-[13px] text-gray-700 whitespace-pre-wrap leading-relaxed" data-testid="terms-text">
          {terms ? terms.text : "Loading…"}
        </div>
        <div className="px-5 py-4 border-t border-gray-100 flex items-center justify-end gap-2">
          <button data-testid="terms-decline" onClick={() => { window.localStorage.clear(); window.location.reload(); }}
            className="px-4 h-10 rounded-lg text-sm font-semibold text-gray-500 hover:text-gray-700">Decline &amp; sign out</button>
          <button data-testid="terms-accept" onClick={accept} disabled={busy || !terms}
            className="px-5 h-10 rounded-lg text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 inline-flex items-center gap-1.5">
            <Check size={15} /> {busy ? "Saving…" : "Accept & continue"}
          </button>
        </div>
      </div>
    </div>
  );
}

// Report a message or a user. `target` = { type: 'message'|'user', id, group_id?, name? }
export function ReportModal({ target, onClose }) {
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  if (!target) return null;
  const submit = () => {
    if (!reason) { toast.error("Please pick a reason"); return; }
    setBusy(true);
    api.post("/reports", { target_type: target.type, target_id: target.id, group_id: target.group_id || null, reason, note })
      .then(() => { toast.success("Report sent to our moderation team"); onClose(); })
      .catch((e) => toast.error(apiError(e, "Could not send report"))).finally(() => setBusy(false));
  };
  return (
    <div className="fixed inset-0 z-[90] bg-slate-900/50 flex items-center justify-center p-4" onClick={onClose} data-testid="report-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2"><Flag size={17} className="text-rose-600" />
            <h3 className="font-bold text-gray-900">Report {target.type === "user" ? target.name || "user" : "message"}</h3></div>
          <button onClick={onClose} data-testid="report-close"><X size={18} className="text-gray-400" /></button>
        </div>
        <div className="px-5 py-4 space-y-2">
          {REASONS.map((r) => (
            <button key={r} data-testid={`report-reason-${r.split(" ")[0].toLowerCase()}`} onClick={() => setReason(r)}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium border ${reason === r ? "border-rose-400 bg-rose-50 text-rose-700" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}>{r}</button>
          ))}
          <textarea data-testid="report-note" value={note} onChange={(e) => setNote(e.target.value)}
            placeholder="Add details (optional)" rows={2}
            className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-200 text-sm resize-none focus:outline-none focus:border-indigo-400" />
        </div>
        <div className="px-5 py-4 border-t border-gray-100 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 h-9 rounded-lg text-sm font-semibold text-gray-500">Cancel</button>
          <button data-testid="report-submit" onClick={submit} disabled={busy}
            className="px-4 h-9 rounded-lg text-sm font-bold text-white bg-rose-600 hover:bg-rose-700 disabled:opacity-50">{busy ? "Sending…" : "Send report"}</button>
        </div>
      </div>
    </div>
  );
}

// Block / unblock a user. Returns a handler that confirms + calls the API.
export async function blockUser(userId, name) {
  await api.post(`/blocks/${userId}`);
  toast.success(`${name || "User"} blocked. Their messages are now hidden.`);
}
export async function unblockUser(userId) {
  await api.delete(`/blocks/${userId}`);
  toast.success("Unblocked");
}

// Blocked-users management list (for the profile modal).
export function BlockedUsers() {
  const [rows, setRows] = useState(null);
  const load = () => api.get("/blocks").then((r) => setRows(r.data.blocked)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  const remove = (id) => unblockUser(id).then(load).catch((e) => toast.error(apiError(e, "Could not unblock")));
  if (rows === null) return <div className="text-xs text-gray-400">Loading…</div>;
  if (!rows.length) return <div className="text-xs text-gray-400" data-testid="blocked-empty">You haven't blocked anyone.</div>;
  return (
    <div className="space-y-1.5" data-testid="blocked-list">
      {rows.map((u) => (
        <div key={u.id} className="flex items-center justify-between px-2.5 py-1.5 rounded-lg bg-gray-50">
          <span className="text-sm font-medium text-gray-700 truncate">{u.name}</span>
          <button data-testid={`unblock-${u.id}`} onClick={() => remove(u.id)}
            className="text-xs font-bold text-indigo-600 hover:text-indigo-700">Unblock</button>
        </div>
      ))}
    </div>
  );
}

export { Ban };
